import { createSelector } from './selector-utils'

/**
 * @param {{
 *   route: string
 *   drugInfoProp: string
 *   mapFn?: (any) => any
 * }} options
 * @returns {{
 *   actionSelect: (id: string, drugProps: any) => any
 *   actionUnselect: (id: string) => any
 *   actionUpdateDosage: (id: string, updates: any) => any
 *   selectDrugInfoRaw: (caseData: any) => { [id: string]: any }
 *   selectDosageRaw: (caseData: any) => { [id: string]: any }
 *   selectDosage: (caseData: any) => { [id: string]: any }
 *   selectSelectedDrugs: (caseData: any) => ({ id: string } & any)[]
 *   selectSelectedDrugsById: (
 *     caseData: any
 *   ) => { [id: string]: { id: string } & any }
 * }}
 */
export const createDoseProp = ({ route, drugInfoProp, mapFn }) => {
  const dosePath = `drugDosage.${route}.doses`

  /**
   * @param {string} path The id
   * @param {any} updates The updates to apply.
   * @returns {any} The changes to apply to the case.
   */
  const actionUpdate = (path, updates) => {
    const changes = {}
    for (const key in updates) {
      const value = updates[key]
      if (value !== undefined) {
        changes[`${path}.${key}`] = updates[key]
      }
    }
    return changes
  }

  const actionSelect = (id, drugProps) => ({
    [`${dosePath}.${id}.time`]: Date.now(),
    ...actionUpdate(`${drugInfoProp}.${id}`, drugProps),
  })

  const actionUnselect = id => ({
    [`${dosePath}.${id}`]: null,
    [`${drugInfoProp}.${id}`]: null,
  })

  const actionUpdateDosage = (id, updates) =>
    actionUpdate(`${dosePath}.${id}`, updates)

  const empty = {}
  const selectDosageRaw = caseData =>
    (caseData.drugDosage &&
      caseData.drugDosage[route] &&
      caseData.drugDosage[route].doses) ||
    empty

  const selectDosage = createSelector(selectDosageRaw, raw => {
    const toReturn = {}
    for (const key in raw) {
      toReturn[key] = mapFn(raw[key])
    }
    return toReturn
  })

  const selectDrugInfoRaw = caseData => caseData[drugInfoProp] || empty

  const selectSelectedDrugs = createSelector(
    selectDosageRaw,
    selectDrugInfoRaw,
    (dosageRaw, drugInfoRaw) => {
      const toReturn = []
      for (const id in dosageRaw) {
        const data = dosageRaw[id]
        const info = drugInfoRaw[id] || {}
        toReturn.push({ id, ...data, ...info })
      }
      return mapFn ? toReturn.map(mapFn) : toReturn
    }
  )

  const selectSelectedDrugsById = createSelector(selectSelectedDrugs, dosage =>
    dosage.reduce((res, item) => {
      res[item.id] = item
      return res
    }, {})
  )

  return {
    actionSelect,
    actionUnselect,
    actionUpdateDosage,
    selectDrugInfoRaw,
    selectDosage,
    selectDosageRaw,
    selectSelectedDrugs,
    selectSelectedDrugsById,
  }
}
